const normalizePermissions = (permissions) => {
  if (Array.isArray(permissions)) {
    return permissions.filter(Boolean);
  }

  if (typeof permissions === "string" && permissions.trim()) {
    return permissions
      .split(",")
      .map((permission) => permission.trim())
      .filter(Boolean);
  }

  return [];
};

const PERMISSION_CATEGORY_MAP = {
  ANALYTICS_VIEW: "Analytics",
  REPORT_VIEW: "Analytics",
  PRODUCT_VIEW: "Products",
  PRODUCT_EDIT: "Products",
  PRODUCTS_VIEW: "Products",
  PRODUCTS_EDIT: "Products",
  USER_VIEW: "Users",
  USER_EDIT: "Users",
  GROUP_VIEW: "Groups",
  GROUP_EDIT: "Groups",
  NOTIFICATION_VIEW: "Notifications",
  NOTIFICATION_EDIT: "Notifications",
  EMAIL_TEMPLATE_VIEW: "Email Templates",
  EMAIL_TEMPLATE_EDIT: "Email Templates",
  RELEASE_DOCUMENT_VIEW: "Release Document",
  RELEASE_DOCUMENT_EDIT: "Release Document",
  CAMPAIGN_VIEW: "Campaigns",
  CAMPAIGN_EDIT: "Campaigns",
  ADMIN_VIEW: "Admin",
  ADMIN_EDIT: "Admin",
};

const PERMISSION_ALIASES = {
  PRODUCTS_VIEW: "PRODUCT_VIEW",
  PRODUCTS_EDIT: "PRODUCT_EDIT",
};

export const labelToPermissionCode = (label = "") =>
  String(label).trim().replace(/\s+/g, "_").toUpperCase();

const createPermissionOption = (label, checked = false) => ({
  label,
  value: PERMISSION_ALIASES[labelToPermissionCode(label)] || labelToPermissionCode(label),
  checked,
});

export const getPermissionValue = (permission) => {
  if (typeof permission === "string") {
    return PERMISSION_ALIASES[permission] || permission;
  }

  if (permission && typeof permission === "object") {
    const code =
      permission.value ||
      permission.code ||
      permission.permission ||
      (permission.label ? labelToPermissionCode(permission.label) : "");

    return PERMISSION_ALIASES[code] || code;
  }

  return "";
};

export const getPermissionLabel = (permission) => {
  if (typeof permission === "string") {
    return String(permission).replace(/_/g, " ").toUpperCase();
  }

  if (permission?.label) {
    return permission.label;
  }

  const value = getPermissionValue(permission);
  return value.replace(/_/g, " ").toUpperCase();
};

export const ADMIN_PERMISSION_GROUPS = [
  {
    title: "Analytics",
    permissions: [createPermissionOption("ANALYTICS VIEW")],
  },
  {
    title: "Campaigns",
    permissions: [
      createPermissionOption("CAMPAIGN VIEW"),
      createPermissionOption("CAMPAIGN EDIT"),
    ],
  },
  {
    title: "Users",
    permissions: [createPermissionOption("USER VIEW")],
  },
  {
    title: "Groups",
    permissions: [
      createPermissionOption("GROUP VIEW"),
      createPermissionOption("GROUP EDIT"),
    ],
  },
  {
    title: "Products",
    permissions: [
      createPermissionOption("PRODUCTS VIEW"),
      createPermissionOption("PRODUCTS EDIT"),
    ],
  },
  {
    title: "Email Templates",
    permissions: [
      createPermissionOption("EMAIL TEMPLATE VIEW"),
      createPermissionOption("EMAIL TEMPLATE EDIT"),
    ],
  },
  {
    title: "Notifications",
    permissions: [
      createPermissionOption("NOTIFICATION VIEW"),
      createPermissionOption("NOTIFICATION EDIT"),
    ],
  },
  {
    title: "Release Document",
    permissions: [
      createPermissionOption("RELEASE DOCUMENT VIEW"),
      createPermissionOption("RELEASE DOCUMENT EDIT"),
    ],
  },
  {
    title: "Admin",
    permissions: [
      createPermissionOption("ADMIN VIEW"),
      createPermissionOption("ADMIN EDIT"),
    ],
  },
];

export const DEFAULT_ADMIN_PERMISSIONS = [
  "CAMPAIGN_VIEW",
  "CAMPAIGN_EDIT",
  "USER_VIEW",
];

export const getAllAdminPermissions = (permissionGroups = ADMIN_PERMISSION_GROUPS) =>
  getAllPermissionsFromGroups(permissionGroups);

export const getAllPermissionsFromGroups = (permissionGroups = []) =>
  permissionGroups
    .flatMap((group) => group.permissions || [])
    .map(getPermissionValue)
    .filter(Boolean);

const groupFlatPermissionsToCategories = (permissions = []) => {
  const grouped = {};

  normalizePermissions(permissions).forEach((permission) => {
    const category = PERMISSION_CATEGORY_MAP[permission] || "Other";
    if (!grouped[category]) {
      grouped[category] = [];
    }
    grouped[category].push(permission);
  });

  return Object.entries(grouped).map(([title, groupPermissions]) => ({
    title,
    permissions: groupPermissions.map((permission) => ({
      label: getPermissionLabel(permission),
      value: getPermissionValue(permission),
    })),
  }));
};

const mapPermissionOption = (permission) => {
  if (typeof permission === "string") {
    return {
      label: getPermissionLabel(permission),
      value: getPermissionValue(permission),
    };
  }

  return {
    label: getPermissionLabel(permission),
    value: getPermissionValue(permission),
    checked: Boolean(permission?.checked),
  };
};

const mapPermissionGroupsArray = (groups = []) => {
  if (!Array.isArray(groups) || groups.length === 0) {
    return [];
  }

  if (typeof groups[0] === "string") {
    return groupFlatPermissionsToCategories(groups);
  }

  return groups
    .map((item) => ({
      title: getPermissionGroupTitle(item),
      permissions: normalizePermissions(
        item.permissions || item.permissionList || item.permission || [],
      ).map(mapPermissionOption),
    }))
    .filter((group) => group.permissions.length > 0);
};

export const getPermissionGroupTitle = (group = {}) =>
  group.title ||
  group.label ||
  group.category ||
  group.moduleName ||
  group.module ||
  group.groupName ||
  "Other";

export const getPermissionGroupLabel = getPermissionGroupTitle;

export const normalizeAdminPermissionGroups = (permissionGroups = []) =>
  mapPermissionGroupsArray(permissionGroups);

export const mapAdminPermissionGroupsFromApi = (response) => {
  const data = response?.data ?? response;

  if (!data) {
    return ADMIN_PERMISSION_GROUPS;
  }

  if (Array.isArray(data)) {
    if (data.length === 0) {
      return ADMIN_PERMISSION_GROUPS;
    }

    if (typeof data[0] === "string") {
      return groupFlatPermissionsToCategories(data);
    }

    const mappedGroups = mapPermissionGroupsArray(data);
    return mappedGroups.length > 0 ? mappedGroups : ADMIN_PERMISSION_GROUPS;
  }

  if (typeof data === "object") {
    return Object.entries(data)
      .map(([title, permissions]) => ({
        title,
        permissions: normalizePermissions(permissions).map(mapPermissionOption),
      }))
      .filter((group) => group.permissions.length > 0);
  }

  return ADMIN_PERMISSION_GROUPS;
};

export const MANAGE_ADMIN_PASSWORD_PLACEHOLDER = "••••••";

const API_PERMISSION_ALIASES = {
  PRODUCTS_VIEW: "PRODUCT_VIEW",
  PRODUCTS_EDIT: "PRODUCT_EDIT",
};

export const normalizeAdminStatus = (status) => {
  if (!status) {
    return "Inactive";
  }

  const normalizedStatus = String(status).toUpperCase();
  if (normalizedStatus === "ACTIVE") {
    return "Active";
  }
  if (normalizedStatus === "INACTIVE") {
    return "Inactive";
  }

  return status;
};

export const mapFormPermissionsToApi = (permissions = []) =>
  [
    ...new Set(
      normalizePermissions(permissions).map(
        (permission) => API_PERMISSION_ALIASES[permission] || permission,
      ),
    ),
  ];

export const extractPermissionsFromApiResponse = (permissions = []) => {
  if (!Array.isArray(permissions) || permissions.length === 0) {
    return [];
  }

  if (
    typeof permissions[0] === "object" &&
    (permissions[0].title || permissions[0].permissions)
  ) {
    return permissions.flatMap((group) =>
      (group.permissions || [])
        .filter((permission) => permission?.checked)
        .map(getPermissionValue),
    );
  }

  return normalizePermissions(permissions).map(getPermissionValue);
};

export const isGroupedPermissionsFormat = (permissions = []) =>
  Array.isArray(permissions) &&
  permissions.length > 0 &&
  typeof permissions[0] === "object" &&
  (permissions[0].title || permissions[0].permissions);

export const mapGroupedPermissionsForDisplay = (permissions = []) => {
  if (!isGroupedPermissionsFormat(permissions)) {
    return normalizeAdminPermissionGroups(permissions);
  }

  return permissions
    .map((group) => ({
      title: getPermissionGroupTitle(group),
      permissions: (group.permissions || [])
        .filter((permission) => permission?.checked)
        .map((permission) => ({
          label: permission.label || getPermissionLabel(permission),
          value: getPermissionValue(permission),
          checked: true,
        })),
    }))
    .filter((group) => group.permissions.length > 0);
};

export const getCheckedPermissionCount = (permissions = []) => {
  if (isGroupedPermissionsFormat(permissions)) {
    return permissions.reduce(
      (count, group) =>
        count +
        (group.permissions || []).filter((permission) => permission?.checked)
          .length,
      0,
    );
  }

  return new Set(normalizePermissions(permissions).map(getPermissionValue)).size;
};

export const mapFormPermissionsToGroupedApi = (
  selectedPermissions = [],
  permissionGroups = ADMIN_PERMISSION_GROUPS,
) => {
  const selectedSet = new Set(
    normalizePermissions(selectedPermissions).map(getPermissionValue),
  );

  return permissionGroups
    .map((group) => {
      const permissions = (group.permissions || []).map((permission) => ({
        label: getPermissionLabel(permission),
        checked: selectedSet.has(getPermissionValue(permission)),
      }));

      if (!permissions.some((permission) => permission.checked)) {
        return null;
      }

      return {
        title: getPermissionGroupTitle(group),
        permissions,
      };
    })
    .filter(Boolean);
};

export const mapStatusToApi = (status) =>
  status === "Active" ? "ACTIVE" : "INACTIVE";

export const buildUpdateAdminPayload = (
  values,
  adminId,
  permissionGroups = ADMIN_PERMISSION_GROUPS,
) => {
  const payload = {
    id: adminId,
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    email: values.email.trim(),
    permissions: mapFormPermissionsToGroupedApi(
      values.permissions,
      permissionGroups,
    ),
    status: mapStatusToApi(values.status),
  };

  const password = values.password?.trim();
  if (password && password !== MANAGE_ADMIN_PASSWORD_PLACEHOLDER) {
    payload.password = password;
  }

  return payload;
};

export const mapAdminListFromApi = (adminList = []) =>
  adminList.map((admin, index) => {
    const groupedPermissions = isGroupedPermissionsFormat(admin.permissions)
      ? admin.permissions
      : null;
    const permissionGroups = groupedPermissions
      ? mapGroupedPermissionsForDisplay(groupedPermissions)
      : normalizeAdminPermissionGroups(admin.permissionGroups);
    const extractedPermissions = extractPermissionsFromApiResponse(
      admin.permissions,
    );
    const permissions = normalizeAdminFormPermissions(
      extractedPermissions.length
        ? extractedPermissions
        : getAllPermissionsFromGroups(permissionGroups),
    );
    const permissionCount = permissionGroups.reduce(
      (count, group) => count + (group.permissions?.length || 0),
      0,
    );

    return {
      key: admin.id ?? index,
      id: admin.id,
      firstName: admin.firstName || "",
      lastName: admin.lastName || "",
      email: admin.email || "",
      status: normalizeAdminStatus(admin.status),
      role: admin.role === "ADMIN" ? "ADMIN" : admin.role || "ADMIN",
      permissions,
      permissionGroups,
      permissionCount,
    };
  });

export const normalizeAdminFormPermissions = (
  permissions = [],
  allowedPermissions,
) => {
  const allowed = allowedPermissions ?? getAllAdminPermissions();

  return [
    ...new Set(
      normalizePermissions(permissions).map(
        (permission) => PERMISSION_ALIASES[permission] || permission,
      ),
    ),
  ].filter((permission) => allowed.includes(permission));
};

export const mapAdminToFormValues = (admin, allowedPermissions) => {
  if (!admin) {
    const defaultPermissions = normalizeAdminFormPermissions(
      DEFAULT_ADMIN_PERMISSIONS,
      allowedPermissions,
    );

    return {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      status: "Active",
      role: "Admin",
      permissions: defaultPermissions.length
        ? defaultPermissions
        : DEFAULT_ADMIN_PERMISSIONS,
    };
  }

  return {
    firstName: admin.firstName || "",
    lastName: admin.lastName || "",
    email: admin.email || "",
    password: MANAGE_ADMIN_PASSWORD_PLACEHOLDER,
    status: admin.status || "Inactive",
    role: admin.role === "ADMIN" ? "Admin" : admin.role || "Admin",
    permissions: normalizeAdminFormPermissions(
      extractPermissionsFromApiResponse(admin.permissions),
      allowedPermissions,
    ),
  };
};

export const groupPermissionsByCategory = (permissions) => {
  const uniquePermissions = [...new Set(normalizePermissions(permissions))];
  const grouped = {};

  uniquePermissions.forEach((permission) => {
    const category = PERMISSION_CATEGORY_MAP[permission] || "Other";
    if (!grouped[category]) {
      grouped[category] = [];
    }
    grouped[category].push(permission);
  });

  return grouped;
};

const getUniquePermissionCount = (permissions, record = {}) => {
  if (typeof record.permissionCount === "number") {
    return record.permissionCount;
  }

  return getCheckedPermissionCount(permissions);
};

export const adminDummyData = [
  {
    key: 1,
    firstName: "Josh",
    lastName: "H",
    email: "admin@PCGS.com",
    status: "Inactive",
    role: "ADMIN",
    permissions: [
      "CAMPAIGN_VIEW",
      "CAMPAIGN_EDIT",
      "USER_VIEW",
      "NOTIFICATION_VIEW",
      "NOTIFICATION_EDIT",
      "ANALYTICS_VIEW",
      "REPORT_VIEW",
    ],
  },
  {
    key: 2,
    firstName: "PJ",
    lastName: "Bonner",
    email: "admin@healthcare2U.com",
    status: "Active",
    role: "ADMIN",
    permissions: [
      "CAMPAIGN_VIEW",
      "CAMPAIGN_EDIT",
      "USER_VIEW",
      "USER_EDIT",
      "NOTIFICATION_VIEW",
      "NOTIFICATION_EDIT",
      "ANALYTICS_VIEW",
      "REPORT_VIEW",
    ],
  },
  {
    key: 3,
    firstName: "John",
    lastName: "Doe",
    email: "product@gmail.com",
    status: "Active",
    role: "ADMIN",
    permissions: [
      "CAMPAIGN_VIEW",
      "CAMPAIGN_EDIT",
      "USER_VIEW",
      "USER_EDIT",
      "NOTIFICATION_VIEW",
      "NOTIFICATION_EDIT",
      "PRODUCT_VIEW",
      "PRODUCT_EDIT",
    ],
  },
  {
    key: 4,
    firstName: "Sam",
    lastName: "K",
    email: "template@gmail.com",
    status: "Active",
    role: "ADMIN",
    permissions: [
      "CAMPAIGN_VIEW",
      "CAMPAIGN_EDIT",
      "USER_VIEW",
      "USER_EDIT",
      "NOTIFICATION_VIEW",
      "NOTIFICATION_EDIT",
      "EMAIL_TEMPLATE_EDIT",
      "GROUP_VIEW",
    ],
  },
];

export const adminColumns = ({ onPermissionClick, onManageClick } = {}) => [
  {
    title: "First Name",
    dataIndex: "firstName",
    key: "firstName",
  },
  {
    title: "Last Name",
    dataIndex: "lastName",
    key: "lastName",
  },
  {
    title: "Email",
    dataIndex: "email",
    key: "email",
    className: "emailtable",
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (status) => (
      <div className={status === "Active" ? "activted" : "inactive"}>
        {status}
      </div>
    ),
  },
  {
    title: "Role",
    dataIndex: "role",
    key: "role",
  },
  {
    title: "Permissions",
    dataIndex: "permissions",
    key: "permissions",
    render: (permissions, record) => {
      const permissionCount = getUniquePermissionCount(permissions, record);

      if (!permissionCount) {
        return null;
      }

      return (
        <button
          type="button"
          className="admin-permissions-link"
          onClick={() => onPermissionClick?.(record)}
        >
          {permissionCount} Permission{permissionCount === 1 ? "" : "s"}
        </button>
      );
    },
  },
  {
    title: "Actions",
    key: "actions",
    render: (record) => (
      <div className="action-icons">
        <button
          type="button"
          className="manage"
          onClick={() => onManageClick?.(record)}
        >
          Manage
        </button>
      </div>
    ),
  },
];

export const filterAdminList = (adminList, searchValue = "") => {
  const query = searchValue.trim().toLowerCase();
  if (!query) {
    return adminList;
  }

  return adminList.filter((admin) => {
    const permissionLabels = (admin.permissionGroups || []).flatMap((group) =>
      (group.permissions || []).map((permission) =>
        getPermissionLabel(permission),
      ),
    );
    const searchableFields = [
      admin.firstName,
      admin.lastName,
      admin.email,
      admin.status,
      admin.role,
      ...normalizePermissions(admin.permissions),
      ...permissionLabels,
    ];

    return searchableFields.some((field) =>
      String(field || "")
        .toLowerCase()
        .includes(query),
    );
  });
};
